import { useContext } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useGlynt from './useGlynt';
import { MarkDispatchContext } from '../../context/MarkContext';
import { IRQCallbacks } from '../../types/my-types';
import { getIdFromUrl } from '../../utils/getIdFromUrl';
import { useAtom } from 'jotai';
import { bulkCorrectionRevertDataAtom } from '../../atoms/spreadsheet-atoms';

export const useCreateCorrection = (
  optimisticUpdate: boolean,
  clearOutBulkCorrectionRevertData: boolean,
  callbacks?: IRQCallbacks,
  syncValidation: boolean = true
) => {
  const glynt = useGlynt();
  const cache = useQueryClient();
  const dispatch = useContext<React.Dispatch<any>>(MarkDispatchContext);
  const [, setBulkCorrectionRevertData] = useAtom(bulkCorrectionRevertDataAtom);

  return useMutation({
    mutationFn: async (data: any) => await (await glynt).correction.create(data, syncValidation),
    ...callbacks,

    onSettled: async (data) => {
      const extractionId = getIdFromUrl(data?.extraction);

      cache.invalidateQueries({
        queryKey: ['extraction-validation', extractionId],
      });
      if (!optimisticUpdate) {
        cache.invalidateQueries({
          queryKey: ['extraction'],
        });
      }
    },

    onMutate: async (data) => {
      // If we issue a correction that is not from reverting a bulk correction, we want to reset the bulk
      // revert data to remove it as an option
      if (clearOutBulkCorrectionRevertData) {
        setBulkCorrectionRevertData([]);
      }

      if (optimisticUpdate) {
        // When making corrections, we can udpate the cache of the extraction data to prevent from needing
        // to re-run the extraction query. There is a timer that will invalidate and thus re-run the extraction
        // query after REFETCH_AFTER_CORRECTION_MS seconds after the last correction is made.

        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await cache.cancelQueries({
          queryKey: ['extraction', data.extraction, { view: 'corrected' }],
        });

        // // Snapshot the previous value
        const previousFields: any = cache.getQueryData([
          'extraction',
          data.extraction,
          { view: 'corrected' },
        ]);

        const previousResults = previousFields?.results || {};

        // Update the corrected value in the cache
        const updatedFields = {
          ...previousFields,
          results: {
            ...previousResults,
            [data.field]: {
              ...previousResults[data.field],
              content: data.correction.content,
              tokens: data.correction.tokens,
            },
          },
        };

        cache.setQueryData(['extraction', data.extraction, { view: 'corrected' }], updatedFields);

        dispatch({ type: 'INCREMENT_CORRECTIONS_COUNT' });

        // Return a context object with the snapshotted value
        return { previousFields };
      }
    },
  });
};
